import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Layers, Loader2, ShieldCheck } from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  hasAuthToken,
  reportsApi,
  type ConsolidatedAccount,
  type ConsolidatedMonth,
  type ConsolidatedReport as ConsolidatedReportData,
  type ConsolidatedSummary,
} from '../../../api';
import type { DateRange } from '../../../lib/dateFilter';
import { WORKSPACE_DATA_EVENT } from '../../../lib/workspaceStore';
import { useChartTheme } from '../../../lib/chartTheme';
import { ExportMenu, type ExportRow } from '../../../lib/exportReports';
import { fmtInr } from '../reportMetrics';

/**
 * Fixed per-account colours so the same account reads the same in every chart,
 * card and table — independent of the workspace colour theme.
 */
const MAIN_COLOR = '#2563eb';
const SELF_COLOR = '#ea580c';
const EXTRA_COLORS = ['#7c3aed', '#0d9488', '#db2777', '#65a30d'];
const COMBINED_COLOR = '#334155';

type Series = { key: string; name: string; roleLabel: string; color: string; summary: ConsolidatedSummary };

function seriesFor(accounts: ConsolidatedAccount[]): Series[] {
  let extra = 0;
  return accounts.map((a) => {
    const color = a.role === 'main' ? MAIN_COLOR : a.role === 'self' ? SELF_COLOR : EXTRA_COLORS[extra++ % EXTRA_COLORS.length];
    const roleLabel = a.role === 'main' ? 'Main Account' : a.role === 'self' ? 'This ledger' : 'Added ledger';
    return { key: `acct_${a.id}`, name: a.name, roleLabel, color, summary: a.summary };
  });
}

type MetricDef = { label: string; hint?: string; pick: (s: ConsolidatedSummary) => number; signed?: boolean };

const METRICS: MetricDef[] = [
  { label: 'Receipts (total collected)', hint: 'EMI + late fees + down-payment balance', pick: (s) => s.receipts.total },
  { label: 'Installment collections', hint: 'EMI portion', pick: (s) => s.installments.emiCollected },
  { label: 'Late fees collected', pick: (s) => s.installments.lateFees },
  { label: 'Interest earned', pick: (s) => s.installments.interest },
  { label: 'Investments (stock bought)', pick: (s) => s.investments.invested },
  { label: 'Investment returns (sales)', pick: (s) => s.investments.returns },
  { label: 'Expenses', pick: (s) => s.expenses.total },
  { label: 'Loans disbursed', pick: (s) => s.payments.loanDisbursed },
  { label: 'Money in (cash + bank)', pick: (s) => s.payments.moneyIn },
  { label: 'Money out (cash + bank)', pick: (s) => s.payments.moneyOut },
  { label: 'Net cash flow', pick: (s) => s.payments.net, signed: true },
];

const MONTHLY_METRICS: { key: keyof Omit<ConsolidatedMonth, 'month'>; label: string }[] = [
  { key: 'receipts', label: 'Receipts' },
  { key: 'installments', label: 'Installment collections' },
  { key: 'investments', label: 'Investments' },
  { key: 'expenses', label: 'Expenses' },
  { key: 'moneyIn', label: 'Money in' },
  { key: 'moneyOut', label: 'Money out' },
];

function fmtSigned(n: number) {
  const r = Math.round(n);
  return `${r < 0 ? '−' : ''}₹${Math.abs(r).toLocaleString('en-IN')}`;
}

function fmtAxis(n: number) {
  const a = Math.abs(n);
  if (a >= 1e7) return `${(n / 1e7).toFixed(1)}Cr`;
  if (a >= 1e5) return `${(n / 1e5).toFixed(1)}L`;
  if (a >= 1e3) return `${(n / 1e3).toFixed(0)}k`;
  return String(n);
}

function monthLabel(ym: string) {
  const [y, m] = ym.split('-').map(Number);
  if (!y || !m) return ym;
  return new Date(y, m - 1, 1).toLocaleDateString('en-IN', { month: 'short', year: '2-digit' });
}

function rangeToInstants(range: DateRange) {
  const from = range.start ? new Date(range.start) : null;
  const to = range.end ? new Date(range.end) : null;
  from?.setHours(0, 0, 0, 0);
  to?.setHours(23, 59, 59, 999);
  return { from: from ? from.toISOString() : null, to: to ? to.toISOString() : null };
}

function Swatch({ color, className = 'h-3 w-3' }: { color: string; className?: string }) {
  return <span className={`inline-block shrink-0 rounded-sm ${className}`} style={{ backgroundColor: color }} aria-hidden />;
}

function AccountLegend({ series }: { series: Series[] }) {
  return (
    <ul className="flex flex-wrap items-center gap-2" aria-label="Account colour legend">
      {series.map((s) => (
        <li
          key={s.key}
          className="inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-white px-3 py-1 text-xs dark:border-zinc-700 dark:bg-zinc-900"
        >
          <Swatch color={s.color} />
          <span className="font-semibold text-zinc-900 dark:text-zinc-100">{s.name}</span>
          <span className="text-zinc-500">· {s.roleLabel}</span>
        </li>
      ))}
      <li className="inline-flex items-center gap-2 rounded-full border border-zinc-300 bg-zinc-50 px-3 py-1 text-xs dark:border-zinc-600 dark:bg-zinc-800">
        <Swatch color={COMBINED_COLOR} />
        <span className="font-semibold text-zinc-900 dark:text-zinc-100">Combined</span>
      </li>
    </ul>
  );
}

function SplitCard({ title, series, pick, combined }: { title: string; series: Series[]; pick: (s: ConsolidatedSummary) => number; combined: number }) {
  const total = series.reduce((s, x) => s + Math.max(0, pick(x.summary)), 0);
  return (
    <div className="app-card app-card-inner min-w-0 border-l-4" style={{ borderLeftColor: COMBINED_COLOR }}>
      <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">{title}</p>
      <p className="mt-1 text-lg font-bold tabular-nums text-zinc-900 dark:text-zinc-100">{fmtInr(combined)}</p>
      <p className="text-[11px] text-zinc-500">Combined total</p>
      <div className="mt-2 flex h-2 w-full overflow-hidden rounded-full bg-zinc-100 dark:bg-zinc-800" aria-hidden>
        {total > 0
          ? series.map((s) => (
              <span key={s.key} style={{ width: `${(Math.max(0, pick(s.summary)) / total) * 100}%`, backgroundColor: s.color }} />
            ))
          : null}
      </div>
      <ul className="mt-2 space-y-1">
        {series.map((s) => (
          <li key={s.key} className="flex items-center justify-between gap-2 text-xs">
            <span className="inline-flex min-w-0 items-center gap-1.5 text-zinc-600 dark:text-zinc-300">
              <Swatch color={s.color} className="h-2.5 w-2.5" />
              <span className="truncate">{s.name}</span>
            </span>
            <span className="font-semibold tabular-nums" style={{ color: s.color }}>
              {fmtInr(pick(s.summary))}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ChartCard({ title, subtitle, children, action }: { title: string; subtitle?: string; children: React.ReactNode; action?: React.ReactNode }) {
  return (
    <div className="app-card app-card-inner min-w-0">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">{title}</h3>
          {subtitle ? <p className="text-xs text-zinc-500">{subtitle}</p> : null}
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

function EmptyChart() {
  return <p className="py-16 text-center text-sm text-zinc-500">No transactions in this period.</p>;
}

export function ConsolidatedReport({
  range,
  rangeLabel,
  onExportRows,
}: {
  range: DateRange;
  rangeLabel: string;
  onExportRows?: (rows: ExportRow[]) => void;
}) {
  const chart = useChartTheme();
  const [report, setReport] = useState<ConsolidatedReportData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [monthlyMetric, setMonthlyMetric] = useState<(typeof MONTHLY_METRICS)[number]['key']>('receipts');

  const { from, to } = useMemo(() => rangeToInstants(range), [range]);

  const load = useCallback(async () => {
    if (!hasAuthToken()) {
      setReport(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      setReport(await reportsApi.consolidated({ from, to, tz: new Date().getTimezoneOffset() }));
    } catch (e) {
      setReport(null);
      setError(e instanceof Error ? e.message : 'Could not load the consolidated summary.');
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    // Ledger switches and data syncs re-scope the report.
    const onData = () => void load();
    window.addEventListener(WORKSPACE_DATA_EVENT, onData);
    return () => window.removeEventListener(WORKSPACE_DATA_EVENT, onData);
  }, [load]);

  const enabled = report?.enabled === true ? report : null;
  const series = useMemo(() => (enabled ? seriesFor(enabled.accounts) : []), [enabled]);
  const combined = enabled?.combined;

  const comparisonData = useMemo(() => {
    const rows: { label: string; pick: (s: ConsolidatedSummary) => number }[] = [
      { label: 'Receipts', pick: (s) => s.receipts.total },
      { label: 'Installments', pick: (s) => s.installments.emiCollected },
      { label: 'Investments', pick: (s) => s.investments.invested },
      { label: 'Expenses', pick: (s) => s.expenses.total },
      { label: 'Loans disbursed', pick: (s) => s.payments.loanDisbursed },
    ];
    return rows.map((r) => ({ name: r.label, ...Object.fromEntries(series.map((s) => [s.key, r.pick(s.summary)])) }));
  }, [series]);

  const paymentsData = useMemo(() => {
    const rows: { label: string; pick: (s: ConsolidatedSummary) => number }[] = [
      { label: 'Cash in', pick: (s) => s.payments.cashIn },
      { label: 'Bank in', pick: (s) => s.payments.bankIn },
      { label: 'Cash out', pick: (s) => s.payments.cashOut },
      { label: 'Bank out', pick: (s) => s.payments.bankOut },
    ];
    return rows.map((r) => ({ name: r.label, ...Object.fromEntries(series.map((s) => [s.key, r.pick(s.summary)])) }));
  }, [series]);

  const monthlyData = useMemo(() => {
    const months = new Set<string>();
    for (const s of series) for (const m of s.summary.monthly) months.add(m.month);
    return [...months].sort().map((month) => {
      const row: Record<string, string | number> = { name: monthLabel(month) };
      for (const s of series) {
        row[s.key] = s.summary.monthly.find((m) => m.month === month)?.[monthlyMetric] ?? 0;
      }
      return row;
    });
  }, [series, monthlyMetric]);

  const receiptShare = useMemo(
    () => series.map((s) => ({ name: s.name, value: s.summary.receipts.total, color: s.color })).filter((x) => x.value > 0),
    [series],
  );

  const exportRows: ExportRow[] = useMemo(() => {
    if (!combined) return [];
    return METRICS.map((m) => ({
      Metric: m.label,
      ...Object.fromEntries(series.map((s) => [`${s.name} (${s.roleLabel})`, Math.round(m.pick(s.summary))])),
      Combined: Math.round(m.pick(combined)),
    }));
  }, [series, combined]);

  useEffect(() => {
    onExportRows?.(exportRows);
  }, [exportRows, onExportRows]);

  if (!enabled || !combined) {
    if (loading && !report) {
      return (
        <div className="app-card app-card-inner flex items-center gap-2 text-sm text-zinc-500">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          Loading consolidated summary…
        </div>
      );
    }
    if (error) {
      return (
        <div className="app-card app-card-inner text-sm text-rose-700" role="alert">
          Consolidated summary unavailable: {error}
        </div>
      );
    }
    return null;
  }

  const tooltipFormatter = (v: number) => fmtSigned(Number(v) || 0);
  const sourceNames = series.map((s) => s.name).join(' + ');

  return (
    <section className="space-y-4 rounded-2xl border border-zinc-200 bg-zinc-50/60 p-3 sm:p-4 dark:border-zinc-700 dark:bg-zinc-900/40" aria-labelledby="consolidated-report-title">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <p className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-zinc-500">
            <Layers className="h-3.5 w-3.5" aria-hidden />
            Consolidated view
          </p>
          <h2 id="consolidated-report-title" className="mt-0.5 text-base font-semibold text-zinc-900 dark:text-zinc-100">
            Consolidated financial summary
          </h2>
          <p className="text-xs text-zinc-500">
            {sourceNames} · {rangeLabel}
            {loading ? ' · refreshing…' : ''}
          </p>
          <p className="mt-1 inline-flex items-start gap-1.5 text-[11px] text-zinc-500">
            <ShieldCheck className="mt-px h-3.5 w-3.5 shrink-0 text-emerald-600" aria-hidden />
            Totals only. Each account&apos;s transactions stay in that account, and no customer or loan details are shared.
          </p>
        </div>
        <div className="w-full shrink-0 sm:w-auto">
          <ExportMenu
            title={`Consolidated Summary — ${sourceNames} (${rangeLabel})`}
            excelFilename="consolidated-summary.xls"
            rows={exportRows}
            className="w-full sm:ml-auto sm:inline-block sm:w-auto"
          />
        </div>
      </header>

      <AccountLegend series={series} />

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4 lg:gap-3">
        <SplitCard title="Receipts" series={series} pick={(s) => s.receipts.total} combined={combined.receipts.total} />
        <SplitCard title="Installment collections" series={series} pick={(s) => s.installments.emiCollected} combined={combined.installments.emiCollected} />
        <SplitCard title="Investments" series={series} pick={(s) => s.investments.invested} combined={combined.investments.invested} />
        <SplitCard title="Expenses" series={series} pick={(s) => s.expenses.total} combined={combined.expenses.total} />
      </div>

      <div className="app-card min-w-0 overflow-hidden">
        <div className="border-b border-zinc-200 bg-zinc-50/80 px-3 py-3 sm:px-4 dark:border-zinc-700 dark:bg-zinc-800/60">
          <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">Account totals</h3>
          <p className="text-xs text-zinc-500">Each account&apos;s own totals, with the combined total in the last column</p>
        </div>
        <div className="app-table-wrap -mx-px overflow-x-auto rounded-none border-0">
          <table className="app-table min-w-[40rem]">
            <thead>
              <tr>
                <th>Metric</th>
                {series.map((s) => (
                  <th key={s.key} className="text-right">
                    <span className="inline-flex items-center justify-end gap-1.5">
                      <Swatch color={s.color} className="h-2.5 w-2.5" />
                      {s.name}
                    </span>
                  </th>
                ))}
                <th className="text-right">
                  <span className="inline-flex items-center justify-end gap-1.5">
                    <Swatch color={COMBINED_COLOR} className="h-2.5 w-2.5" />
                    Combined
                  </span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100 bg-white dark:bg-transparent">
              {METRICS.map((m) => (
                <tr key={m.label}>
                  <td>
                    <span className="font-medium text-zinc-900 dark:text-zinc-100">{m.label}</span>
                    {m.hint ? <span className="block text-[11px] text-zinc-500">{m.hint}</span> : null}
                  </td>
                  {series.map((s) => (
                    <td key={s.key} className="text-right font-semibold tabular-nums" style={{ color: s.color }}>
                      {m.signed ? fmtSigned(m.pick(s.summary)) : fmtInr(m.pick(s.summary))}
                    </td>
                  ))}
                  <td className="text-right font-bold tabular-nums text-zinc-900 dark:text-zinc-100">
                    {m.signed ? fmtSigned(m.pick(combined)) : fmtInr(m.pick(combined))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <ChartCard title="Account comparison" subtitle={rangeLabel}>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={comparisonData}>
              <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
              <XAxis dataKey="name" tick={{ fontSize: 10 }} stroke={chart.axis} interval={0} />
              <YAxis tick={{ fontSize: 11 }} stroke={chart.axis} tickFormatter={fmtAxis} />
              <Tooltip formatter={tooltipFormatter} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {series.map((s) => (
                <Bar key={s.key} dataKey={s.key} name={s.name} fill={s.color} radius={[4, 4, 0, 0]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Monthly trend by account"
          subtitle="Stacked — bar height is the combined total"
          action={
            <select
              aria-label="Monthly metric"
              className="app-field !w-auto !py-1 text-xs"
              value={monthlyMetric}
              onChange={(e) => setMonthlyMetric(e.target.value as typeof monthlyMetric)}
            >
              {MONTHLY_METRICS.map((m) => (
                <option key={m.key} value={m.key}>
                  {m.label}
                </option>
              ))}
            </select>
          }
        >
          {monthlyData.length ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke={chart.axis} />
                <YAxis tick={{ fontSize: 11 }} stroke={chart.axis} tickFormatter={fmtAxis} />
                <Tooltip formatter={tooltipFormatter} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {series.map((s) => (
                  <Bar key={s.key} dataKey={s.key} name={s.name} stackId="monthly" fill={s.color} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChart />
          )}
        </ChartCard>

        <ChartCard title="Share of combined receipts" subtitle={fmtInr(combined.receipts.total)}>
          {receiptShare.length ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={receiptShare} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={95}>
                  {receiptShare.map((r) => (
                    <Cell key={r.name} fill={r.color} />
                  ))}
                </Pie>
                <Tooltip formatter={tooltipFormatter} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChart />
          )}
        </ChartCard>
      </div>

      <ChartCard title="Payments report — cash and bank movement" subtitle="Money in: collections and sales · Money out: loans, stock purchases and expenses">
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={paymentsData}>
            <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
            <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke={chart.axis} />
            <YAxis tick={{ fontSize: 11 }} stroke={chart.axis} tickFormatter={fmtAxis} />
            <Tooltip formatter={tooltipFormatter} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {series.map((s) => (
              <Bar key={s.key} dataKey={s.key} name={s.name} fill={s.color} radius={[4, 4, 0, 0]} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>
    </section>
  );
}
